import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, Circle, FileText, User, PenLine, Rocket, Upload } from "lucide-react";

const PASSOS = [
  { key: "documento", label: "Documento da Empresa", icon: FileText },
  { key: "representante", label: "Representante", icon: User },
  { key: "contrato", label: "Contrato", icon: PenLine },
  { key: "configurar", label: "Configurar operação", icon: Rocket },
];

export default function AtivacaoEmpresa() {
  const { token } = useParams();
  const [st, setSt] = useState(null);
  const [erro, setErro] = useState(null);
  const [busy, setBusy] = useState(false);
  const [arquivo, setArquivo] = useState(null);
  const [rep, setRep] = useState({ nome: "", cpf: "", cargo: "", email: "", telefone: "" });
  const [contrato, setContrato] = useState(null);
  const [lendo, setLendo] = useState(false);
  const [checks, setChecks] = useState({ a: false, b: false });
  const [otpInfo, setOtpInfo] = useState(null);
  const [codigo, setCodigo] = useState("");
  const [concluido, setConcluido] = useState(null);

  const load = () => api.get(`/ativacao/${token}`).then(r => setSt(r.data))
    .catch(e => setErro(formatApiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const enviarDocumento = async () => {
    if (!arquivo) { toast.error("Selecione o PDF do Contrato Social"); return; }
    const fd = new FormData(); fd.append("arquivo", arquivo);
    setBusy(true);
    try {
      await api.post(`/ativacao/${token}/contrato-social`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Contrato Social enviado — pode continuar");
      setArquivo(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const salvarRepresentante = async () => {
    setBusy(true);
    try {
      await api.post(`/ativacao/${token}/representante`, rep);
      toast.success("Representante registrado");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const lerContrato = async () => {
    try {
      const r = await api.get(`/ativacao/${token}/contrato`);
      setContrato(r.data); setLendo(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const pedirCodigo = async () => {
    if (!checks.a || !checks.b) { toast.error("Confirme as duas declarações"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/ativacao/${token}/otp`, {});
      setOtpInfo(r.data);
      toast.success("Código enviado" + (r.data.canais.whatsapp ? " por WhatsApp" : "") + (r.data.canais.email ? " e e-mail" : ""));
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const assinar = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/ativacao/${token}/assinar`, {
        codigo, declara_representante: checks.a, li_concordo: checks.b });
      setConcluido(r.data); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (erro) return (
    <div className="min-h-screen flex items-center justify-center p-6 text-center">
      <div><p className="text-lg font-bold">Link inválido</p><p className="text-sm text-muted-foreground mt-2">{erro}</p></div>
    </div>);
  if (!st) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Carregando…</div>;

  const p = st.progresso;
  const etapa = !p.documento ? "documento" : !p.representante ? "representante" : !p.contrato ? "contrato" : "configurar";

  return (
    <div className="min-h-screen bg-white" data-testid="ativacao-page">
      <header className="border-b border-border">
        <div className="max-w-2xl mx-auto px-6 py-5 flex items-center gap-3">
          <div className="w-7 h-7 brand-bg" />
          <span className="font-heading font-extrabold">MontaJá</span>
        </div>
      </header>
      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="label-cap">{st.empresa.nome} · CNPJ {st.empresa.cnpj}</div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">ATIVE SUA EMPRESA NO MONTAJÁ</h1>
        <p className="text-sm text-muted-foreground mt-1">Leva apenas alguns minutos.</p>

        {st.documentacao_status === "correcao_necessaria" && (
          <div className="mt-4 border-l-4 border-red-500 bg-red-50 p-3 text-xs" data-testid="ativacao-correcao">
            <strong>Ajuste solicitado pela equipe MontaJá:</strong> {st.correcao_motivo}
            <br />Reenvie o documento corrigido abaixo. Você pode continuar configurando sua operação normalmente.
          </div>
        )}

        <div className="flex gap-2 mt-6 flex-wrap" data-testid="ativacao-progresso">
          {PASSOS.map((s, i) => (
            <div key={s.key} className={`flex items-center gap-1.5 text-xs px-3 py-2 border ${
              p[s.key] ? "border-emerald-300 bg-emerald-50 text-emerald-700" :
              etapa === s.key ? "border-[#142766] bg-[#142766] text-white" : "border-border text-muted-foreground"}`}>
              {p[s.key] ? <CheckCircle2 size={13} /> : <Circle size={13} />} {i + 1}. {s.label}
            </div>
          ))}
        </div>

        {/* PASSO 1 — Contrato Social */}
        <div className="border border-border p-5 mt-6" data-testid="ativacao-documento">
          <div className="label-cap mb-1"><FileText size={13} className="inline mr-1" />1 · Documento da Empresa</div>
          <p className="text-xs text-muted-foreground mb-3">
            Contrato Social, última alteração contratual consolidada ou documento societário equivalente (PDF).
            O documento permanece <strong>privado</strong> — visível só à equipe MontaJá.
          </p>
          {st.documento && (
            <div className="text-xs bg-emerald-50 border border-emerald-200 p-2 mb-3" data-testid="documento-enviado">
              ✓ Enviado: <strong>{st.documento.nome_original}</strong> (v{st.documento.versao}) — status: {st.documento.status}
            </div>
          )}
          <div className="flex gap-2 items-center flex-wrap">
            <input type="file" accept=".pdf" onChange={e => setArquivo(e.target.files?.[0] || null)}
              className="text-xs" data-testid="ativacao-arquivo" />
            <Button onClick={enviarDocumento} disabled={busy || !arquivo} className="btn-sharp brand-bg text-white"
              data-testid="ativacao-anexar">
              <Upload size={14} /> ANEXAR CONTRATO SOCIAL
            </Button>
          </div>
        </div>

        {/* PASSO 2 — Representante */}
        {p.documento && (
          <div className="border border-border p-5 mt-4" data-testid="ativacao-representante">
            <div className="label-cap mb-1"><User size={13} className="inline mr-1" />2 · Quem representará a Empresa no MontaJá?</div>
            <p className="text-xs text-muted-foreground mb-3">Deve ser pessoa com representação empresarial: sócio, sócio-administrador, administrador ou representante legal.</p>
            {st.representante ? (
              <div className="text-xs bg-emerald-50 border border-emerald-200 p-2" data-testid="representante-ok">
                ✓ {st.representante.nome} — {st.cargos[st.representante.cargo] || st.representante.cargo} · {st.representante.email_mascarado}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label className="label-cap">Nome completo *</Label>
                    <Input value={rep.nome} onChange={e => setRep({ ...rep, nome: e.target.value })} className="mt-1" data-testid="rep-nome" /></div>
                  <div><Label className="label-cap">CPF *</Label>
                    <Input value={rep.cpf} onChange={e => setRep({ ...rep, cpf: e.target.value })} className="mt-1" data-testid="rep-cpf" /></div>
                  <div><Label className="label-cap">Cargo/Qualificação *</Label>
                    <select value={rep.cargo} onChange={e => setRep({ ...rep, cargo: e.target.value })}
                      className="mt-1 w-full h-9 border border-border px-2 text-sm bg-white" data-testid="rep-cargo">
                      <option value="">Selecione…</option>
                      {Object.entries(st.cargos).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select></div>
                  <div><Label className="label-cap">E-mail *</Label>
                    <Input type="email" value={rep.email} onChange={e => setRep({ ...rep, email: e.target.value })} className="mt-1" data-testid="rep-email" /></div>
                  <div><Label className="label-cap">WhatsApp/Telefone *</Label>
                    <Input value={rep.telefone} onChange={e => setRep({ ...rep, telefone: e.target.value })} className="mt-1" data-testid="rep-telefone" /></div>
                </div>
                <Button onClick={salvarRepresentante} disabled={busy || !rep.nome || !rep.cpf || !rep.cargo || !rep.email || !rep.telefone}
                  className="btn-sharp brand-bg text-white" data-testid="rep-salvar">Continuar</Button>
              </div>
            )}
          </div>
        )}

        {/* PASSO 3 — Contrato */}
        {p.representante && !p.contrato && (
          <div className="border-2 border-[#142766] p-5 mt-4" data-testid="ativacao-contrato">
            <div className="label-cap mb-1"><PenLine size={13} className="inline mr-1" />3 · CONTRATO EMPRESA × MONTAJÁ</div>
            <Button onClick={lerContrato} variant="outline" className="btn-sharp mt-2" data-testid="ler-contrato">
              LER CONTRATO COMPLETO
            </Button>
            {lendo && contrato && (
              <>
                <div className="mt-3 border border-border bg-secondary/40 p-4 max-h-80 overflow-y-auto whitespace-pre-wrap text-xs"
                  data-testid="contrato-texto">{contrato.texto}</div>
                <div className="mt-4 space-y-2 text-xs">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={checks.a} onChange={e => setChecks({ ...checks, a: e.target.checked })}
                      className="mt-0.5" data-testid="check-representante" />
                    <span>Declaro que sou o representante identificado neste contrato, na qualidade informada, e que possuo poderes para representar a Empresa.</span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={checks.b} onChange={e => setChecks({ ...checks, b: e.target.checked })}
                      className="mt-0.5" data-testid="check-concordo" />
                    <span>Li, compreendi e concordo integralmente com o Contrato Empresa × MontaJá.</span>
                  </label>
                </div>
                {!otpInfo ? (
                  <Button onClick={pedirCodigo} disabled={busy || !checks.a || !checks.b}
                    className="btn-sharp w-full mt-4 h-11 bg-[#FF6700] hover:bg-[#e05a00] text-white font-bold"
                    data-testid="assinar-continuar">ASSINAR E CONTINUAR</Button>
                ) : (
                  <div className="mt-4 border border-border p-4" data-testid="otp-bloco">
                    <div className="label-cap">Confirme sua assinatura</div>
                    <p className="text-xs text-muted-foreground mt-1" data-testid="otp-canais-info">
                      {(otpInfo.canais?.email || otpInfo.canais?.whatsapp) ? <>Enviamos um código para: </> : <>Não foi possível enviar o código por nenhum canal. </>}
                      {otpInfo.canais?.email && <><strong>{otpInfo.email_mascarado}</strong></>}
                      {otpInfo.canais?.email && otpInfo.canais?.whatsapp && <> e </>}
                      {otpInfo.canais?.whatsapp && <>WhatsApp final <strong>{otpInfo.telefone_final}</strong></>}
                    </p>
                    <div className="flex gap-2 mt-3">
                      <Input value={codigo} onChange={e => setCodigo(e.target.value)} placeholder="Código (6 dígitos)"
                        maxLength={6} className="w-40 text-center tracking-widest font-bold" data-testid="otp-codigo" />
                      <Button onClick={assinar} disabled={busy || codigo.length !== 6}
                        className="btn-sharp brand-bg text-white" data-testid="otp-confirmar">CONFIRMAR ASSINATURA</Button>
                      <Button onClick={pedirCodigo} disabled={busy} variant="ghost" className="text-xs" data-testid="otp-reenviar">Reenviar</Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* PASSO 4 — Concluído */}
        {(p.contrato || concluido) && (
          <div className="border-2 border-emerald-400 bg-emerald-50 p-6 mt-4 text-center" data-testid="ativacao-concluida">
            <CheckCircle2 size={36} className="mx-auto text-emerald-600" />
            <p className="font-heading font-extrabold text-xl mt-2">TUDO CERTO!</p>
            <p className="text-sm mt-1">Sua documentação foi enviada para conferência.<br />
              Enquanto isso, você já pode configurar sua operação no MontaJá.</p>
            <Link to="/login">
              <Button className="btn-sharp brand-bg text-white mt-4 h-11 px-8" data-testid="comecar-configuracao">
                COMEÇAR CONFIGURAÇÃO
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
